import PersonIcon from "@mui/icons-material/Person";
import {
  Autocomplete,
  Box,
  CircularProgress,
  createFilterOptions,
  ListSubheader,
  Popper,
  PopperProps,
  TextField,
  Typography,
} from "@mui/material";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ListChildComponentProps, VariableSizeList as List } from "react-window";

// Define the option interface
interface Option {
  id: number;
  name: string;
  vendorCode: string;
  firstLetter: string;
}

// Custom filter function
const filter = createFilterOptions<Option>({
  matchFrom: "any",
  stringify: (option: Option) => `${option.name} ${option.vendorCode}`,
});

// Custom Popper component
const VirtualizedPopper = React.forwardRef<HTMLDivElement, PopperProps>(function VirtualizedPopper(props, ref) {
  const { children, style, anchorEl, ...other } = props;

  return (
    <Popper
      anchorEl={anchorEl}
      {...other}
      style={{
        ...style,
        width: anchorEl ? (anchorEl as HTMLElement).clientWidth : "auto",
      }}
      placement="bottom-start"
      ref={ref}
    >
      {children}
    </Popper>
  );
});

// Constants
const LISTBOX_PADDING = 8;
const ITEM_HEIGHT = 56;
const GROUP_HEADER_HEIGHT = 48;

// Define props interface for VirtualizedListDirect
interface VirtualizedListDirectProps extends React.HTMLAttributes<HTMLElement> {
  groupedOptions: Option[];
  onOptionClick: (option: Option) => void;
  selectedOption: Option | null;
  focusedOptionIndex: number;
  setFocusedOptionIndex: (index: number) => void;
  isUsingKeyboard: boolean;
  setIsUsingKeyboard: (value: boolean) => void;
}

// Virtualized list with sticky headers
const VirtualizedListDirect = React.forwardRef<HTMLDivElement, VirtualizedListDirectProps>(
  function VirtualizedListDirect(props, ref) {
    const {
      groupedOptions,
      onOptionClick,
      selectedOption,
      focusedOptionIndex,
      setFocusedOptionIndex,
      isUsingKeyboard,
      setIsUsingKeyboard,
      ...other
    } = props;
    const [stickyHeaderText, setStickyHeaderText] = useState<string>("");
    const [showStickyHeader, setShowStickyHeader] = useState<boolean>(false);
    const listRef = useRef<List>(null);
    const [hoveredIndex, setHoveredIndex] = useState<number>(-1);

    // Group options by firstLetter
    const groupedItems = useMemo(() => {
      const items: Array<{
        type: "group" | "option";
        data: string | Option;
        groupIndex: number;
        optionIndex?: number;
      }> = [];
      let currentGroup = "";
      let groupIndex = 0;
      let optionIndex = 0;

      groupedOptions.forEach((option) => {
        const group = option.firstLetter?.toUpperCase();
        if (group !== currentGroup) {
          currentGroup = group;
          items.push({ type: "group", data: group, groupIndex });
          groupIndex++;
        }
        items.push({ type: "option", data: option, groupIndex: groupIndex - 1, optionIndex });
        optionIndex++;
      });

      return items;
    }, [groupedOptions]);

    // Get unique groups
    const groups = useMemo(() => {
      return [...new Set(groupedOptions.map((option) => option.firstLetter?.toUpperCase()))];
    }, [groupedOptions]);

    const getItemSize = (index: number) => {
      return groupedItems[index]?.type === "group" ? GROUP_HEADER_HEIGHT : ITEM_HEIGHT;
    };

    // Scroll to focused item when it changes
    useEffect(() => {
      if (listRef.current && focusedOptionIndex >= 0) {
        // Find the index in groupedItems that corresponds to this option
        const itemIndex = groupedItems.findIndex(
          (item) => item.type === "option" && item.optionIndex === focusedOptionIndex
        );

        if (itemIndex >= 0) {
          // Use auto instead of smart for immediate scrolling
          listRef.current.scrollToItem(itemIndex, "auto");
        }
      }
    }, [focusedOptionIndex, groupedItems]);

    // Handle scroll for sticky header
    const handleScroll = useCallback(
      ({ scrollOffset }: { scrollOffset: number }) => {
        if (groupedItems.length === 0) return;

        let currentOffset = 0;
        let currentGroupIndex = 0;

        for (let i = 0; i < groupedItems.length; i++) {
          const itemHeight = getItemSize(i);

          if (currentOffset + itemHeight > scrollOffset) {
            const item = groupedItems[i];
            currentGroupIndex = item.type === "group" ? item.groupIndex : item.groupIndex;
            break;
          }
          currentOffset += itemHeight;
        }

        const currentGroup = groups[currentGroupIndex];
        setStickyHeaderText(currentGroup || "");
        setShowStickyHeader(scrollOffset > 0 && !!currentGroup);
      },
      [groupedItems, groups]
    );

    const renderItem = ({ index, style }: ListChildComponentProps) => {
      const item = groupedItems[index];

      if (!item) return null;

      if (item.type === "group") {
        return (
          <ListSubheader
            style={{
              ...style,
              height: GROUP_HEADER_HEIGHT,
              fontWeight: 600,
              fontSize: "0.875rem",
              paddingLeft: "8px",
            }}
            component="div"
          >
            {item.data as string}
          </ListSubheader>
        );
      }

      const option = item.data as Option;
      const isSelected = selectedOption?.id === option.id;
      const isFocused = focusedOptionIndex === item.optionIndex;
      const isHovered = hoveredIndex === item.optionIndex;

      return (
        <Box
          component="li"
          style={{
            ...style,
            height: ITEM_HEIGHT,
            display: "flex",
            alignItems: "center",
            padding: "8px 16px",
            cursor: "pointer",
            borderBottom: "1px solid #f0f0f0",
            backgroundColor: isFocused ? "#f5f5f5" : isHovered ? "#f5f5f5" : isSelected ? "#e3f2fd" : "#fff",
          }}
          onClick={() => onOptionClick(option)}
          onMouseDown={(e) => e.preventDefault()}
          onMouseEnter={() => {
            if (item.optionIndex !== undefined) {
              setHoveredIndex(item.optionIndex);
              // Reset keyboard navigation when hovering
              if (isUsingKeyboard) {
                setIsUsingKeyboard(false);
                setFocusedOptionIndex(-1);
              }
            }
          }}
          onMouseLeave={() => {
            setHoveredIndex(-1);
          }}
        >
          <Box sx={{ flex: 1, overflow: "hidden", minWidth: 0 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 400,
                color: isSelected ? "#1976d2" : "inherit",
                whiteSpace: "nowrap", // one line only
                overflow: "hidden", // hide overflow
                textOverflow: "ellipsis",
              }}
            >
              {option.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: isSelected ? "#1565c0" : "text.secondary",
              }}
            >
              Vendor Code: {option.vendorCode}
            </Typography>
          </Box>
        </Box>
      );
    };

    const totalHeight = Math.min(
      350,
      groupedItems.reduce((acc, _, index) => acc + getItemSize(index), 0) + 2 * LISTBOX_PADDING
    );

    return (
      <Box ref={ref} {...other} sx={{ position: "relative" }}>
        {/* Sticky Header */}
        {showStickyHeader && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: GROUP_HEADER_HEIGHT,
              display: "flex",
              alignItems: "center",
              backgroundColor: "#fff",
              fontWeight: 600,
              fontSize: "0.875rem",
              color: "#666",
              //   borderBottom: "2px solid #d0d0d0",
              zIndex: 10,
              paddingLeft: "8px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            {stickyHeaderText}
          </Box>
        )}

        <List
          ref={listRef}
          height={totalHeight}
          width="100%"
          itemCount={groupedItems.length}
          itemSize={getItemSize}
          overscanCount={5}
          onScroll={handleScroll}
        >
          {renderItem}
        </List>
      </Box>
    );
  }
);

VirtualizedListDirect.displayName = "VirtualizedListDirect";

interface VirtualizedAutocompleteProps {
  options: Option[];
  value: Option | null;
  onChange: (event: React.SyntheticEvent, value: Option | null) => void;
  usedModule: string;
  loading: boolean;
  defaultValue?: Option | null;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
}

const VirtualizedAutocomplete: React.FC<VirtualizedAutocompleteProps> = ({
  options,
  value,
  onChange,
  usedModule = "taskBoard",
  loading,
  defaultValue,
  disabled = false,
  placeholder = "Select Partner",
  label = "Select Partner",
}) => {
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);
  const [hasUserTyped, setHasUserTyped] = useState(false);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState<number>(-1);
  const [isUsingKeyboard, setIsUsingKeyboard] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Always show all options when dropdown is open, filter only when typing
  const groupedOptions = useMemo(() => {
    let filtered: Option[];

    // If user has typed something different from the selected value
    if (hasUserTyped && inputValue && (!value || inputValue !== value.name)) {
      filtered = filter(options, { inputValue, getOptionLabel: (option) => option.name });
    } else {
      // Show all options when dropdown is open without typing
      filtered = options;
    }

    return filtered.sort((a, b) => {
      if (a.firstLetter !== b.firstLetter) {
        return a.firstLetter.localeCompare(b.firstLetter);
      }
      return a.name.localeCompare(b.name);
    });
  }, [options, inputValue, value, hasUserTyped]);

  const handleOptionClick = (option: Option) => {
    onChange?.(null as any, option);
    setInputValue(option.name);
    setOpen(false);
    setHasUserTyped(false); // Reset typing flag when option is selected
  };

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!open) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setIsUsingKeyboard(true);
          setFocusedOptionIndex((prev) => {
            const newIndex = prev < groupedOptions.length - 1 ? prev + 1 : 0;
            return newIndex;
          });
          break;
        case "ArrowUp":
          event.preventDefault();
          setIsUsingKeyboard(true);
          setFocusedOptionIndex((prev) => {
            const newIndex = prev > 0 ? prev - 1 : groupedOptions.length - 1;
            return newIndex;
          });
          break;
        case "Home":
          event.preventDefault();
          setIsUsingKeyboard(true);
          setFocusedOptionIndex(0);
          break;
        case "End":
          event.preventDefault();
          setIsUsingKeyboard(true);
          setFocusedOptionIndex(groupedOptions.length - 1);
          break;
        case "Enter":
          event.preventDefault();
          if (focusedOptionIndex >= 0 && focusedOptionIndex < groupedOptions.length) {
            handleOptionClick(groupedOptions[focusedOptionIndex]);
          }
          break;
        case "Escape":
          event.preventDefault();
          setOpen(false);
          break;
      }
    },
    [open, focusedOptionIndex, groupedOptions, handleOptionClick]
  );

  useEffect(() => {
    if (inputRef.current) {
      const input = inputRef.current;
      input.addEventListener("keydown", handleKeyDown);
      return () => {
        input.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [handleKeyDown]);

  // Reset focused index when opening dropdown
  useEffect(() => {
    if (open) {
      // If there's a selected value, focus it; otherwise focus the first option
      const selectedIndex = value ? groupedOptions.findIndex((opt) => opt.id === value.id) : -1;
      setFocusedOptionIndex(selectedIndex >= 0 ? selectedIndex : -1);
      setIsUsingKeyboard(false);
    }
  }, [open, value, groupedOptions]);

  // Override the default filtering behavior to always show all options
  const customFilterOptions = () => {
    return groupedOptions; // Always return all grouped options
  };

  const CustomListbox = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(function CustomListbox(
    props,
    ref
  ) {
    const { ...other } = props;
    return (
      <VirtualizedListDirect
        {...other}
        ref={ref}
        groupedOptions={groupedOptions}
        onOptionClick={handleOptionClick}
        selectedOption={value}
        focusedOptionIndex={focusedOptionIndex}
        setFocusedOptionIndex={setFocusedOptionIndex}
        isUsingKeyboard={isUsingKeyboard}
        setIsUsingKeyboard={setIsUsingKeyboard}
      />
    );
  });

  return (
    <Autocomplete
      disabled={disabled}
      defaultValue={defaultValue}
      size="small"
      sx={{
        width: "100%",
        minWidth: "400px",
        "& fieldset": { borderRadius: usedModule === "taskBoard" ? "4px" : "24px" },
      }}
      options={options} // Pass all original options
      value={value}
      open={open}
      onOpen={() => {
        setOpen(true);
        setHasUserTyped(false); // Reset typing flag when opening
      }}
      onClose={() => {
        setOpen(false);
        setHasUserTyped(false); // Reset typing flag when closing
      }}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue, reason) => {
        setInputValue(newInputValue);
        // Set hasUserTyped to true only when user actually types
        if (reason === "input") {
          setHasUserTyped(true);
        } else if (reason === "reset" || reason === "clear") {
          setHasUserTyped(false);
        }
      }}
      onChange={(event, newValue) => {
        onChange?.(event, newValue);
        if (newValue) {
          setInputValue(newValue.name);
          setHasUserTyped(false);
        }
      }}
      filterOptions={customFilterOptions} // Use our custom filter that shows all options
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      PopperComponent={VirtualizedPopper}
      ListboxComponent={CustomListbox}
      disableListWrap
      freeSolo={false}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          label={label}
          placeholder={placeholder}
          variant="outlined"
          fullWidth
          InputProps={{
            ...params.InputProps,
            startAdornment: <PersonIcon color="primary" />,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};

export { VirtualizedAutocomplete };
export type { Option };
